/**
 * Metric lookup, formatting and the contract-data hooks behind the
 * leaderboards.
 *
 * Split out of `ContractLeaders.tsx` so that file exports only components.
 * A non-component export from a `.tsx` module disables React Fast Refresh
 * for it, so every edit would force a full page reload and lose the page's
 * ticker, scroll position and query cache.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchContractLeaders } from '../../services/contractLeadersService';
import type { ContractLeadersResponse } from '../../services/contractLeadersService';
import {
  fetchSymbolSummary,
  toSymbolHeader,
  toTickerStats,
} from '../../services/symbolSummaryService';
import type { SymbolHeader } from '../../services/symbolSummaryService';
import { fetchCompanyProfile } from '../../services/companyProfileService';
import type { CompanyProfile } from '../../services/companyProfileService';
import { fetchTopContracts } from '../../services/topContractsService';
import type { TopContractsBoard } from './topContracts';
import { fmtCompactNum } from './format';
import { EXPLORER_CACHE } from './cachePolicy';
import type { TickerStats } from '../../services/optionContractsService';

/**
 * Contract-level leaderboards for the Overview: session volume, open interest
 * and open-interest change.
 *
 * **LIVE** — `services/contractLeadersService.ts`, one call to QuantForge's
 * `GET /v1/contract-leaders`, which returns all three rankings.
 *
 * ⚠️ **Ranking is the server's, not ours.** Each list is ranked over the whole
 * chain. The previous implementation fetched one volume-ranked snapshot and
 * re-sorted it locally for the two OI panels — which cannot work, because a
 * contract with heavy open interest and quiet volume never entered the slice
 * to be re-sorted. Nothing here sorts.
 */

/* ── columns ────────────────────────────────────────────────────────── */

export type ColumnKey =
  | 'type'
  | 'strike'
  | 'expiry'
  | 'volume'
  | 'openInterest'
  | 'prevOpenInterest'
  | 'oiChange'
  | 'impliedVolatility'
  | 'lastPrice'
  | 'orders'
  | 'premium';

/**
 * What every column reader is handed.
 *
 * ⚠️ **Structural on purpose, not a union of the two wire types.** The tables
 * are fed by two endpoints — `/v1/contract-leaders` and `/v1/top-contracts` —
 * whose rows overlap on identity and volume but diverge after that. A union
 * would force each reader to narrow before touching a field the other row does
 * not carry, for no gain: a reader asked for a field its row lacks should give
 * the same dash it gives for a null, which optional fields already produce.
 *
 * A panel is responsible for passing columns its rows can answer. Asking for
 * `oiChange` on a top-contracts row is not a type error; it renders dashes.
 */
export interface MetricRow {
  strike: number;
  volume?: number | null;
  open_interest?: number | null;
  prev_open_interest?: number | null;
  oi_change?: number | null;
  implied_volatility?: number | null;
  last_price?: number | null;
  orders?: number | null;
  premium?: number | null;
}

export interface ColumnDef {
  label: string;
  /** Reads the value off a row. Null renders as a dash. */
  read: (row: MetricRow) => number | null;
  /** Renders a sign and colours by direction. */
  signed?: boolean;
  /** Formatter for the magnitude. Defaults to a plain integer count. */
  format?: (n: number) => string;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * The columns, keyed by the name panels ask for.
 *
 * ⚠️ **Field names are read directly, with no candidate-key fallback.** The
 * previous version tried a list of aliases per metric (`total_volume`,
 * `volume`, `vol`…) because the feed's names were not pinned down. They are
 * now — `/v1/contract-leaders` publishes exactly these. A fallback list here
 * would only serve to hide a genuine wire-shape change behind a silent dash.
 *
 * ⚠️ **Net Premium is gone, and is not to be reinstated from a nearby field.**
 * The old panels carried a Net Premium column that the leaders endpoint does
 * not serve. It is a directional figure with a sign convention that is easy to
 * get backwards — see the `net-premium-directional` skill — so the honest move
 * is to drop the column rather than fill it from whichever numeric field looks
 * closest. `bid_pct`/`mid_pct`/`ask_pct` are on the row if an aggression
 * column is wanted later; they are NOT net premium.
 */
export const LEADER_COLUMNS: Record<ColumnKey, ColumnDef> = {
  // Rendered by dedicated branches in ContractLeaders; the reader is unused
  // for these three and exists only so the record is total over ColumnKey.
  type: { label: 'Type', read: () => null },
  strike: { label: 'Strike', read: (r) => r.strike },
  expiry: { label: 'Expiry', read: () => null },

  volume: { label: 'Volume', read: (r) => r.volume ?? null, format: fmtCompactNum },
  openInterest: { label: 'OI', read: (r) => r.open_interest ?? null },
  prevOpenInterest: { label: 'Prev OI', read: (r) => r.prev_open_interest ?? null },
  oiChange: { label: 'OI Change', read: (r) => r.oi_change ?? null, signed: true },

  impliedVolatility: {
    label: 'IV',
    read: (r) => r.implied_volatility ?? null,
    // Arrives as a fraction — 0.4715 is 47.15%.
    format: (n) => `${(n * 100).toFixed(1)}%`,
  },
  lastPrice: {
    label: 'Last',
    read: (r) => r.last_price ?? null,
    format: (n) => n.toFixed(2),
  },

  /**
   * ⚠️ **QF ALERT count, not orders on the tape.**
   *
   * The wire calls this `orders` and the reference design labels the column
   * ORDERS, so the label is kept — but the number counts how many times the
   * fuzion detector alerted on this contract this session. "6" is six alerts,
   * not six orders traded. Never 0: a row exists because it alerted.
   *
   * Do not describe this as order flow anywhere in copy.
   */
  orders: { label: 'Orders', read: (r) => r.orders ?? null },

  /**
   * ⚠️ **Gross premium traded, and deliberately NOT signed.**
   *
   * Every dollar that traded on the contract this session, with no side
   * convention applied — and note it is the CONTRACT's premium, not the
   * alerts': a row with one alert can legitimately show millions.
   *
   * It is not net premium — see the warning above about the Net Premium column
   * that was removed rather than approximated, and do not derive one by
   * diffing the two panels. Marking this `signed: true` would colour it
   * bull/bear and assert a direction the number does not carry; the call/put
   * split comes from which panel a row is in.
   *
   * ⚠️ **A genuine `0` is renderable and must not be filtered.** An alerted
   * contract absent from the tape aggregate sits at 0, ranked last, so the row
   * count keeps matching the alert count.
   */
  premium: { label: 'Prems', read: (r) => r.premium ?? null, format: fmtCompactNum },
};

/* ── row bars ───────────────────────────────────────────────────────── */

/**
 * How far the widest bar runs across the row.
 *
 * Short of the full width so the top row still reads as a table row with a
 * tint behind it rather than a solid block of colour, which at full bleed
 * swallows the text sitting on it.
 */
export const BAR_MAX_PCT = 92;

/**
 * The largest absolute value of `column` across `rows`, or 0 if there is none.
 *
 * ⚠️ **Callers pass the DISPLAYED slice, not the full fetched list.** Scaling to
 * a row that was cut leaves the visible top row short of full width, which reads
 * as "there is something bigger off-screen" — true, but not what a panel
 * captioned as the top ten is claiming.
 *
 * Absolute values, so a signed column cannot produce a negative scale and
 * invert every bar.
 */
export function barScale(rows: MetricRow[], column: ColumnKey | undefined): number {
  if (!column) return 0;
  const { read } = LEADER_COLUMNS[column];
  return rows.reduce((max, row) => {
    const v = read(row);
    return v == null || !Number.isFinite(v) ? max : Math.max(max, Math.abs(v));
  }, 0);
}

/**
 * One row's share of `scale`, as a percentage width for its tint.
 *
 * Null when there is no bar to draw — no bar column, no usable scale, or a row
 * the feed did not value. A null value gets no bar rather than a zero-width
 * one: the cell beside it already renders a dash, and a hairline of tint would
 * read as a real, very small figure.
 */
export function barWidthPct(
  row: MetricRow,
  column: ColumnKey | undefined,
  scale: number
): number | null {
  if (!column || scale <= 0) return null;
  const value = LEADER_COLUMNS[column].read(row);
  if (value == null || !Number.isFinite(value)) return null;
  // Clamped rather than left to CSS. A gradient stop past 100% is clipped by
  // the browser anyway, so an overflowing row renders correctly by accident —
  // which means a caller that scaled to the wrong set produces a table of
  // identical full-width bars and no visible symptom. Capping here keeps the
  // returned number honest for anything that reads it.
  return Math.min(Math.abs(value) / scale, 1) * BAR_MAX_PCT;
}

export function fmtLeaderValue(value: number, def: ColumnDef): string {
  const body = def.format ? def.format(Math.abs(value)) : fmtInt(Math.abs(value));
  if (!def.signed) return def.format ? def.format(value) : fmtInt(value);
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${body}`;
}

/* ── data source ─────────────────────────────────────── */

/**
 * All three leaderboards for a symbol, in one request.
 *
 * The panels are three rankings of one response, so they cost one request
 * between them rather than one each.
 *
 * ⚠️ **No `remainingWindow` here, unlike the old snapshot hook.** That one
 * measured its stale window from the data's own `as_of` stamp, because the
 * snapshot could arrive most of a window old and the two windows would
 * otherwise stack. This response carries no timestamp for its own age — only
 * `session_date`, which is the session covered, not when the row was built.
 * Inventing an age from `session_date` would compute "hours old" for data
 * refreshed a minute ago. The plain `EXPLORER_CACHE` window applies, and QF's
 * own 60s TTL still gates whether a refetch changes anything.
 */
export interface ContractLeadersState {
  data: ContractLeadersResponse | null;
  isLoading: boolean;
  isError: boolean;
}

export function useContractLeaders(symbol: string, enabled: boolean): ContractLeadersState {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['contractLeaders', symbol.toUpperCase()],
    queryFn: () => fetchContractLeaders({ symbol, limit: LEADER_LIMIT }),
    ...EXPLORER_CACHE,
    enabled: !!symbol && enabled,
    retry: 1,
  });

  // ⚠️ The failure state is carried out of here on purpose. Returning only the
  // data leaves the panels unable to tell a failed fetch from a slow one, and
  // they render "loading" for both — a request that died stays on screen as a
  // spinner forever, which is the one failure nobody investigates.
  return { data: data ?? null, isLoading, isError };
}

/**
 * Rows requested per ranking.
 *
 * The panels show fifteen. Fifty is fetched so that a reader who later wants
 * the list extended is not gated on a round trip, and because the three
 * rankings share one response — trimming to exactly what is displayed would
 * save one payload, not three.
 */
const LEADER_LIMIT = 50;

/**
 * The session's top contracts by directional net premium, calls and puts —
 * one request.
 *
 * **LIVE** — `services/topContractsService.ts`, QF PR #1186.
 *
 * ⚠️ **The candidate set is the TAPE, not the alert store.** Every contract
 * that traded is eligible; `orders` is an alert count joined on as an
 * annotation and is 0 on most rows. This changed at QF's #975 — the panels
 * were previously alert-gated, which is why they returned one or two rows on
 * most tickers. See the service for the full semantics.
 *
 * Both panels are two rankings of one response, so they cost one request
 * between them, matching `useContractLeaders` above.
 */
export interface TopContractsState {
  data: TopContractsBoard | null;
  isLoading: boolean;
  isError: boolean;
}

export function useTopContracts(symbol: string, enabled: boolean): TopContractsState {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['topContracts', symbol.toUpperCase()],
    queryFn: () => fetchTopContracts(symbol, { limit: TOP_CONTRACTS_LIMIT }),
    ...EXPLORER_CACHE,
    staleTime: TOP_CONTRACTS_STALE_MS,
    enabled: !!symbol && enabled,
    retry: 1,
  });

  // The failure state is carried out deliberately — see the note in
  // `useContractLeaders`. A dead request that renders as "loading" is a
  // spinner nobody investigates.
  return { data: data ?? null, isLoading, isError };
}

/**
 * ⚠️ **The one deliberate departure from `EXPLORER_CACHE`'s single window.**
 *
 * `cachePolicy.ts` argues, correctly, that the window should not vary by data
 * type — its reasoning is that a refetch costs one flat request whatever sits
 * behind it, so the client has no reason to care. That holds for the snapshot
 * endpoints it was written for.
 *
 * This board is different in kind rather than in cost: alert counts and premium
 * both move *during* the session, and QF serves it `Cache-Control: max-age=15`
 * behind a 30s server-side cache. Holding it for ten minutes would show a
 * reader an alert count that the source refreshed thirty-nine times since.
 *
 * Thirty seconds matches the server's own TTL — polling faster gains nothing,
 * because a refetch inside that window returns the identical cached board.
 *
 * The rest of `EXPLORER_CACHE` still applies: this is a mount-time check, not a
 * background timer, and focus/reconnect refetches stay off so the numbers never
 * rewrite themselves under someone reading them.
 */
const TOP_CONTRACTS_STALE_MS = 30 * 1000;

/**
 * Rows requested per side.
 *
 * Ten, matching the reference design and the server's own default. The bars
 * scale to the largest row in the displayed set, so fetching deeper than the
 * panels show would not change what is drawn.
 *
 * ⚠️ Server range is 1–50; outside it is a 422, not a clamp.
 */
const TOP_CONTRACTS_LIMIT = 10;

/**
 * The whole Overview card in one request — `GET /v1/symbol-summary`.
 *
 * **LIVE** — `services/symbolSummaryService.ts`.
 *
 * ⚠️ **Deliberately not derived from the leaderboards above.**
 * `/v1/contract-leaders` returns a top slice of each ranking, not the whole
 * chain — summing those rows would produce totals that look authoritative and
 * are quietly short, and a put/call built from a volume-ranked subset is biased
 * toward whichever side traded hardest today, which is the very thing the
 * ratio is meant to measure. This is one row covering the whole tape.
 *
 * The query key is shared by `useSymbolHeader` below, so the hero bar, the
 * Range column, the Volume column and the Options Flow column are all served
 * by a single fetch.
 */
function useSymbolSummary(symbol: string, enabled: boolean) {
  return useQuery({
    queryKey: ['symbolSummary', symbol.toUpperCase()],
    queryFn: () => fetchSymbolSummary(symbol),
    ...EXPLORER_CACHE,
    enabled: !!symbol && enabled,
    retry: 1,
  });
}

/**
 * The company's name and classification — `GET /v1/company-profile`.
 *
 * **Deliberately a separate request from `useSymbolSummary`.** QuantForge
 * serves this at `max-age=3600` against symbol-summary's `max-age=15`: the
 * name is static and re-sending it with every price refresh is waste. Two
 * cadences, two calls.
 *
 * ⚠️ **401s our REST bearer today** — QF still mounts this router cookie-only.
 * See `companyProfileService.ts`. `retry: false` because a gate rejection will
 * not become a 200 on the second attempt, and the header renders without a
 * name rather than showing an error.
 */
export function useCompanyProfile(symbol: string): CompanyProfile | null {
  const { data } = useQuery({
    queryKey: ['companyProfile', symbol.toUpperCase()],
    queryFn: () => fetchCompanyProfile(symbol),
    ...EXPLORER_CACHE,
    enabled: !!symbol,
    retry: false,
  });

  return data ?? null;
}

/**
 * Session-wide figures for the Volume and Options Flow columns.
 *
 * Null while loading or on failure — the columns render dashes, which is also
 * the correct display for a symbol the provider has no row for. A 200 with
 * null fields and a failed request are deliberately not distinguished here:
 * both mean "nothing to show", and the endpoint degrades per group rather
 * than failing, so a partly-dashed card is expected under degradation.
 */
export function useTickerStats(symbol: string, enabled: boolean): TickerStats | null {
  const { data } = useSymbolSummary(symbol, enabled);
  return data ? toTickerStats(data) : null;
}

/**
 * Header and Range figures — price, 5-day change, market cap, 52-week band.
 *
 * Shares `useTickerStats`'s request. Percentages are already converted from
 * the wire's fractions; see `toSymbolHeader`.
 */
export function useSymbolHeader(symbol: string, enabled: boolean = true): SymbolHeader | null {
  const { data } = useSymbolSummary(symbol, enabled);
  return data ? toSymbolHeader(data) : null;
}
